import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, CheckCircle, AlertCircle, Clock, FileText, Download } from 'lucide-react';
import { FileProcessingInfo, FileUploadData, FinancialStatement, ParsingStatus } from '../types';
import { getFieldCategories, uploadFileToClient, getClient, downloadClientFile } from '../services/api';
import SourceDataEditor from './SourceDataEditor';

interface UnifiedFileUploadProps {
    clientId?: string;
}

const UnifiedFileUpload: React.FC<UnifiedFileUploadProps> = ({
    clientId
}) => {
    const [uploadedFiles, setUploadedFiles] = useState<Record<string, FileUploadData>>({});
    const [parsingStatuses, setParsingStatuses] = useState<Record<string, ParsingStatus>>({});
    const [fileIds, setFileIds] = useState<Record<string, string>>({}); // Map file names to file IDs
    const [isUploading, setIsUploading] = useState(false);
    const [fieldCategories, setFieldCategories] = useState<Record<string, string[]>>({});
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [statements, setStatements] = useState<FinancialStatement[]>([]);

    const handleUploadComplete = useCallback((fileId: string, processingInfo: FileProcessingInfo): void => {
        // Handle successful upload completion
        console.log(`File ${fileId} uploaded and processed successfully:`, processingInfo);

        // Update parsing status to completed
        setParsingStatuses(prev => ({
            ...prev,
            [fileId]: {
                fileName: uploadedFiles[fileId]?.name || '',
                status: 'completed',
                progress: 100,
            }
        }));

        setStatements(processingInfo.statements);
    }, [uploadedFiles]);

    const handleUploadError = useCallback((fileId: string, error: string): void => {
        // Update parsing status to error
        setParsingStatuses(prev => ({
            ...prev,
            [fileId]: {
                fileName: uploadedFiles[fileId]?.name || '',
                status: 'error',
                progress: 0,
                error
            }
        }));
    }, [uploadedFiles]);

    // Load field categories on component mount
    useEffect(() => {
        const loadFieldCategories = async () => {
            try {
                const response = await getFieldCategories();
                if (response.success && response.data) {
                    setFieldCategories(response.data);
                }
            } catch (error) {
                console.error('Failed to load field categories:', error);
            } finally {
                setLoadingCategories(false);
            }
        };

        loadFieldCategories();
    }, []);

    // Load existing files when clientId is provided
    useEffect(() => {
        const loadExistingFiles = async () => {
            if (!clientId) {
                return;
            }

            try {
                const response = await getClient(clientId);
                const client = response.client;

                if (client.files && client.files.length > 0) {
                    const filesData: Record<string, FileUploadData> = {};
                    const statusData: Record<string, ParsingStatus> = {};
                    const ids: Record<string, string> = {};

                    client.files.forEach(file => {
                        // Only show financial files in UnifiedFileUpload
                        if (file.category === 'financials' || !file.category) {
                            const fileKey = file.file_name;

                            filesData[fileKey] = {
                                name: file.file_name,
                                content: '', // We don't store the raw content, just show that file exists
                                size: file.file_size || 0
                            };

                            statusData[fileKey] = {
                                fileName: file.file_name,
                                status: 'completed',
                                progress: 100
                            };

                            if (file.id) {
                                ids[fileKey] = file.id;
                            }
                        }
                    });

                    setUploadedFiles(filesData);
                    setParsingStatuses(statusData);
                    setFileIds(ids);
                }

                // Set statements from client
                if (client.statements) {
                    setStatements(client.statements);
                }
            } catch (error) {
                console.error('Failed to load existing files:', error);
            }
        };

        loadExistingFiles();
    }, [clientId]);

    const handleFileUpload = useCallback(async (fileId: string, fileData?: FileUploadData) => {
        if (fileData == null) {
            // Remove file
            setUploadedFiles(prev => {
                const newFiles = { ...prev };
                delete newFiles[fileId];
                return newFiles;
            });
            return;
        }

        try {
            // Update status to parsing
            setParsingStatuses(prev => ({
                ...prev,
                [fileId]: {
                    ...prev[fileId],
                    status: 'parsing',
                    progress: 50
                }
            }));

            // Update local state immediately for better UX
            setUploadedFiles(prev => ({
                ...prev,
                [fileId]: fileData
            }));

            // If we have a client ID, save to backend
            if (clientId) {
                const response = await uploadFileToClient(clientId, fileData.name, fileData.content);

                // Store file ID from response
                if (response.file_id) {
                    setFileIds(prev => ({
                        ...prev,
                        [fileId]: response.file_id!
                    }));
                }

                // Handle successful upload with parsing info
                if (response.processing_info) {
                    handleUploadComplete(fileId, response.processing_info);
                }
            }

        } catch (error) {
            setParsingStatuses(prev => ({
                ...prev,
                [fileId]: {
                    ...prev[fileId],
                    status: 'error',
                    error: (error as Error).message
                }
            }));
            handleUploadError(fileId, (error as Error).message);
        }
    }, [clientId, handleUploadError, handleUploadComplete]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setIsUploading(true);

        acceptedFiles.forEach(file => {
            const fileExtension = file.name.toLowerCase().split('.').pop();

            // Use filename as unique identifier since we don't detect file type
            const fileId = file.name;

            // Set initial parsing status
            setParsingStatuses(prev => ({
                ...prev,
                [fileId]: {
                    fileName: file.name,
                    status: 'uploading',
                    progress: 0
                }
            }));

            if (fileExtension === 'csv') {
                // Read CSV as text
                const reader = new FileReader();
                reader.onload = (e) => {
                    const content = e.target?.result as string;
                    if (content) {
                        const fileData: FileUploadData = {
                            name: file.name,
                            content: content,
                            file: file,
                            size: file.size
                        };
                        handleFileUpload(fileId, fileData);
                    } else {
                        handleUploadError(fileId, 'Error reading file content');
                    }
                };
                reader.onerror = () => {
                    handleUploadError(fileId, 'Error reading file: ' + reader.error?.message);
                };
                reader.readAsText(file);
            } else if (fileExtension === 'xlsx' || fileExtension === 'xls' || fileExtension === 'pdf') {
                // Read Excel as binary and convert to base64
                const reader = new FileReader();
                reader.onload = (e) => {
                    const arrayBuffer = e.target?.result as ArrayBuffer;
                    if (arrayBuffer) {
                        const uint8Array = new Uint8Array(arrayBuffer);
                        const binary = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
                        const base64Content = btoa(binary);

                        const fileData: FileUploadData = {
                            name: file.name,
                            content: base64Content,
                            file: file,
                            size: file.size
                        };
                        handleFileUpload(fileId, fileData);
                    } else {
                        handleUploadError(fileId, 'Error reading file content');
                    }
                };
                reader.onerror = () => {
                    handleUploadError(fileId, 'Error reading file: ' + reader.error?.message);
                };
                reader.readAsArrayBuffer(file);
            } else {
                handleUploadError(fileId, 'Unsupported file format. Please upload PDF, CSV or Excel files.');
            }
        });

        setIsUploading(false);
    }, [handleUploadError, handleFileUpload]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
            'application/vnd.ms-excel.sheet.binary.macroEnabled.12': ['.xlsb'],
        },
        multiple: true
    });

    const handleRemove = (fileId: string): void => {
        handleFileUpload(fileId, undefined);
        setParsingStatuses(prev => {
            const newStatuses = { ...prev };
            delete newStatuses[fileId];
            return newStatuses;
        });
        setFileIds(prev => {
            const newIds = { ...prev };
            delete newIds[fileId];
            return newIds;
        });
    };

    const handleDownload = async (fileId: string) => {
        if (!clientId) return;

        const actualFileId = fileIds[fileId];
        if (!actualFileId) {
            console.error('File ID not found for download');
            alert('File ID not found. Please refresh the page and try again.');
            return;
        }

        try {
            await downloadClientFile(clientId, actualFileId);
        } catch (error) {
            console.error('Failed to download file:', error);
            alert('Failed to download file. Please try again.');
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getStatusIcon = (status: ParsingStatus['status']) => {
        switch (status) {
            case 'uploading':
            case 'parsing':
                return <Clock className="w-4 h-4 text-blue-600 animate-spin" />;
            case 'completed':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'error':
                return <AlertCircle className="w-4 h-4 text-red-600" />;
            default:
                return <FileText className="w-4 h-4 text-gray-400" />;
        }
    };

    const getStatusColor = (status: ParsingStatus['status']) => {
        switch (status) {
            case 'uploading':
            case 'parsing':
                return 'border-blue-200 bg-blue-50';
            case 'completed':
                return 'border-green-200 bg-green-50';
            case 'error':
                return 'border-red-200 bg-red-50';
            default:
                return 'border-gray-200 bg-gray-50';
        }
    };

    const totalFiles = Object.keys(uploadedFiles).length;
    const completedFiles = Object.values(parsingStatuses).filter(status => status.status === 'completed').length;

    if (loadingCategories) {
        return (
            <div className="card">
                <div className="card-header">
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <h2 className="card-title">Financial Documents</h2>
                    </div>
                </div>
                <div className="p-8 text-center">
                    <Clock className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading field categories...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <h2 className="card-title">Financial Documents</h2>
                    </div>
                    <div className="text-sm text-gray-600">
                        {completedFiles} of {totalFiles} files processed
                    </div>
                </div>
            </div>

            {/* Upload Area */}
            <div
                {...getRootProps()}
                className={`
                    border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors mb-6
                    ${isDragActive
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }
                    ${isUploading ? 'opacity-50 pointer-events-none' : ''}
                `}
            >
                <input {...getInputProps()} />

                <div className="flex flex-col items-center gap-4">
                    <Upload className="w-12 h-12 text-gray-400" />

                    <div>
                        <p className="text-lg font-medium text-gray-900">
                            {isDragActive ? 'Drop files here' : 'Upload Financial Documents'}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            Drag and drop multiple files or click to select
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            Supported formats: PDF, CSV, Excel (.pdf, .csv, .xlsx)
                        </p>
                    </div>
                </div>
            </div>

            {/* Field Categories Display - Grouped by Year */}
            <SourceDataEditor
                statements={statements}
                fieldCategories={fieldCategories}
            />

            {/* File Status List */}
            <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Upload Status</h3>

                {Object.entries(uploadedFiles).map(([fileId, uploadedFile]) => {
                    const parsingStatus = parsingStatuses[fileId];

                    return (
                        <div key={fileId} className={`border rounded-lg p-4 ${getStatusColor(parsingStatus?.status || 'pending')}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    {getStatusIcon(parsingStatus?.status || 'pending')}
                                    <h4 className="font-medium text-gray-900">
                                        {uploadedFile.name}
                                    </h4>
                                </div>

                                <div className="flex items-center gap-1">
                                    {parsingStatus?.status === 'completed' && fileIds[fileId] && (
                                        <button
                                            onClick={() => handleDownload(fileId)}
                                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                            type="button"
                                            title="Download file"
                                        >
                                            <Download className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleRemove(fileId)}
                                        className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                                        type="button"
                                        title="Remove file"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-700">
                                        {uploadedFile.file ? formatFileSize(uploadedFile.file.size) : 'Unknown size'}
                                    </span>
                                    <span className="text-gray-500">
                                        {uploadedFile.name.split('.').pop()?.toUpperCase()}
                                    </span>
                                </div>

                                {parsingStatus && (
                                    <div className="space-y-2">
                                        {/* Progress Bar */}
                                        {parsingStatus.status === 'uploading' || parsingStatus.status === 'parsing' ? (
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${parsingStatus.progress}%` }}
                                                ></div>
                                            </div>
                                        ) : null}

                                        {/* Status Message */}
                                        <div className="flex items-center justify-between text-xs">
                                            <div>
                                                {parsingStatus.status === 'uploading' && (
                                                    <span className="text-blue-600">Uploading file...</span>
                                                )}
                                                {parsingStatus.status === 'parsing' && (
                                                    <span className="text-blue-600">Parsing financial data...</span>
                                                )}
                                                {parsingStatus.status === 'completed' && (
                                                    <span className="text-green-600">✓ Successfully parsed</span>
                                                )}
                                                {parsingStatus.status === 'error' && (
                                                    <span className="text-red-600">✗ {parsingStatus.error}</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Processing Info */}
                                        {parsingStatus.processingInfo && (
                                            <div className="text-xs text-gray-500 bg-white/50 p-2 rounded">
                                                {parsingStatus.processingInfo.validation.warnings && parsingStatus.processingInfo.validation.warnings.length > 0 && (
                                                    <div className="text-yellow-600 mt-1">
                                                        Warnings: {parsingStatus.processingInfo.validation.warnings.join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">File Format Requirements</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Files must be in PDF, CSV or Excel format</li>
                    <li>• First row should contain column headers</li>
                    <li>• Use numeric values only (no currency symbols)</li>
                    <li>• Use periods (.) for decimal separators</li>
                    <li>• Leave empty cells for missing values</li>
                    <li>• Fields will be automatically categorized by the system</li>
                    <li>• Multiple files can contain fields from different statement types</li>
                </ul>
            </div>
        </div>
    );
};

export default UnifiedFileUpload;
